import runtimeEnv from './runtimeEnv.js';
import eyeson, { immediate } from '@eyeson/js';

// https://www.enablex.io/insights/what-is-multi-camera/

let _instance = null;
let _accessKey = null;
let _currentState = null;
let _storedLayout = null;
let _isCurrentPresentationLayout = false;
let _listener = [];
const _state = {
  init: 'init',
  active: 'active',
  error: 'error',
};
const _defaultOptions = {
  fitContent: 'zoom',
  quality: null,
  screenshareUserPreventLayout: false,
};
const _options = Object.assign({}, _defaultOptions);
let _meetingOptions = { layout_available: true };

const registerUser = () => {
  return new Promise((resolve, reject) => {
    if (_accessKey) {
      resolve();
      return;
    }
    if (!_instance) {
      _instance = eyeson.createInstance();
    }
    const onEvent = (event) => {
      if (event.type === 'guest_user') {
        _accessKey = event.token;
        _instance.offEvent(onEvent);
        resolve();
      } else if (event.type === 'error') {
        _instance.offEvent(onEvent);
        reject(new Error(event.content));
      }
    };
    try {
      _instance.onEvent(onEvent);
      _instance.send({
        type: 'request_guest_user',
        api: eyeson.config.api,
        token: eyeson.room.guest_token,
        name: '[2nd Cam] ' + eyeson.user.name,
      });
    } catch (error) {
      if (_instance) {
        _instance.offEvent(onEvent);
      }
      reject(error);
    }
  });
};

const onInstanceEvent = (event) => {
  const { type } = event;
  if (type === 'room_ready') {
    if (_currentState === _state.init) {
      setCurrentState(_state.active);
      if (!_instance.user.apiId) {
        _instance.user.apiId = event.content.user.id;
      }
      _meetingOptions = event.content.options;
      storeCurrentLayout(event.content.options);
      if (!_options.screenshareUserPreventLayout) {
        setPresentationLayout();
      }
      emit({ type: 'second_camera_active' });
    }
  } else if (type === 'options_update') {
    _meetingOptions = event.options;
    onLayoutUpdate(event.options);
  } else if (type === 'error') {
    setCurrentState(_state.error);
    emit({ type: 'warning', name: 'error:msg:unknown' });
    stop();
  } else if (type === 'exit') {
    stop();
  }
};

const start = async (options) => {
  setCurrentState(_state.init);
  emit({ type: 'second_camera_init' });
  try {
    await registerUser();
  } catch (error) {
    setCurrentState(_state.error);
    const name =
      error instanceof Error ? `error_${error.name}` : 'error:msg:unknown';
    emit({ type: 'warning', name });
    return;
  }
  try {
    _instance.onEvent(onInstanceEvent);
    startSession(options);
  } catch (error) {
    setCurrentState(_state.error);
    emit({ type: 'warning', name: 'error:msg:unknown' });
    stop();
  }
};

const apply = (options, fitContent) => {
  if (fitContent !== _options.fitContent) {
    _options.fitContent = fitContent;
    setPresentationLayout();
  }
  _instance.send(Object.assign({ type: 'start_stream' }, options));
};

const setCurrentState = (state) => {
  _currentState = state;
};

const getCurrentState = () => {
  return _currentState;
};

const startSession = (options) => {
  if (_options.quality === 'low') {
    options.sendEncodings = {
      video: { maxBitrate: +runtimeEnv.REACT_APP_DATASAVER_LOW_VIDEO },
    };
  }
  if (_options.quality === 'medium') {
    options.sendEncodings = {
      video: { maxBitrate: +runtimeEnv.REACT_APP_DATASAVER_MEDIUM_VIDEO },
    };
  }
  _instance.start(_accessKey, options);
};

const stop = () => {
  if (_instance) {
    _instance.offEvent(onInstanceEvent);
    if (_instance.core) {
      clearInterval(_instance.core.pollingFallbackInterval);
    }
    _instance.destroy();
  }
  if (_currentState) {
    setCurrentState(null);
    if (!_options.screenshareUserPreventLayout) {
      restorePreviousLayout();
    }
    emit({ type: 'second_camera_ended' });
  }
};

const storeCurrentLayout = (options) => {
  if (options.layout_map) {
    try {
      _storedLayout = {
        layout: options.layout,
        name: options.layout_name,
        users: options.layout_users,
        map: JSON.stringify(options.layout_map),
        show_names: options.show_names,
        voice_activation: options.voice_activation,
      };
    } catch (error) {
      _storedLayout = {
        layout: 'auto',
        show_names: options.show_names,
        voice_activation: options.voice_activation,
      };
    }
  } else {
    _storedLayout = {
      layout: options.layout,
      name: options.layout_name,
      users: options.layout_users,
      show_names: options.show_names,
      voice_activation: options.voice_activation,
    };
  }
};

const restorePreviousLayout = () => {
  if (_storedLayout) {
    if (_meetingOptions.layout_available) {
      eyeson.send({ type: 'set_layout', params: _storedLayout });
    }
    clearStoredLayout();
  }
};

const clearStoredLayout = () => {
  _storedLayout = null;
};

const setPresentationLayout = () => {
  if (_meetingOptions.layout_available) {
    const name =
      _options.fitContent === 'contain'
        ? 'present-upper-6-aspect-fit'
        : 'present-upper-6';
    eyeson.send({
      type: 'set_layout',
      params: {
        layout: 'auto',
        name,
        users: [_instance.user.apiId, eyeson.user.apiId, '', '', '', '', ''],
        voice_activation: true,
        show_names: false,
      },
    });
  }
};

const onLayoutUpdate = (options) => {
  if (_currentState !== _state.active) {
    _isCurrentPresentationLayout = false;
    return;
  }
  const { layout, layout_name, layout_users } = options;
  const name =
    _options.fitContent === 'contain'
      ? 'present-upper-6-aspect-fit'
      : 'present-upper-6';
  _isCurrentPresentationLayout =
    layout === 'auto' &&
    layout_name === name &&
    layout_users &&
    layout_users.length === 7 &&
    layout_users[0] === _instance.user.apiId &&
    layout_users[1] === eyeson.user.apiId;
};

const isCurrentPresentationLayout = () => {
  return _isCurrentPresentationLayout;
};

const onEvent = (fn) => {
  _listener.push(fn);
};

const offEvent = (fn) => {
  if (fn) {
    _listener = _listener.filter((cb) => cb !== fn);
  } else {
    _listener.length = 0;
  }
};

const emit = (msg) => {
  immediate(() => {
    _listener.forEach((fn) => {
      if (typeof fn === 'function') {
        fn(msg);
      }
    });
  });
};

const setOptions = (options) => {
  Object.assign(_options, options);
};

const reset = () => {
  stop();
  Object.assign(_options, _defaultOptions);
};

const _helper = {
  start,
  stop,
  apply,
  getCurrentState,
  setPresentationLayout,
  clearStoredLayout,
  isCurrentPresentationLayout,
  onEvent,
  offEvent,
  setOptions,
  reset,
};

export default _helper;
